import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import { CONTACT } from '@/mocks/contactInfo';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { article, breadcrumb } from '@/lib/seo';

export default function ArticleRat() {
  useDocumentHead({
    title: 'Rat dans la maison : que faire immédiatement (guide)',
    description: 'Un rat chez vous en Guadeloupe ? Les gestes immédiats, les erreurs à éviter, et comment savoir si c\'est un rat isolé ou une colonie installée. Guide complet.',
    ogTitle: 'Rat dans la maison : que faire immédiatement — Gwada Nuisibles',
    jsonLd: [article({ headline: 'Rat dans la maison : que faire immédiatement', description: 'Gestes immédiats, erreurs à éviter, rat isolé ou colonie.', path: '/blogue/rat-dans-la-maison-que-faire' }), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Blogue', path: '/blogue' }, { name: 'Rat dans la maison', path: '/blogue/rat-dans-la-maison-que-faire' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Blogue',href:'/blogue'},{label:'Rat dans la maison : que faire'}]} />

      {/* BANDEAU TITRE */}
      <section className="bg-primary-500 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Link
            to="/blogue"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Retour au blogue
          </Link>
          <h1 className="font-heading font-black text-2xl md:text-4xl text-white leading-tight max-w-3xl">
            Rat dans la maison : que faire immédiatement
          </h1>
        </div>
      </section>

      {/* CONTENU */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <article className="max-w-2xl mx-auto">
            <RevealWrapper>
              <div className="prose-custom space-y-8">

                {/* INTRO */}
                <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
                  Un bruit de course dans le galetas à 2 h du matin, une silhouette qui file le long du mur de la cuisine : pas de panique, mais pas d&rsquo;attentisme non plus. En Guadeloupe, un rat de passage devient une colonie en quelques semaines — climat idéal, reproduction toute l&rsquo;année. Voici le plan d&rsquo;action, dans l&rsquo;ordre.
                </p>

                {/* IMAGE ILLUSTRATION */}
                <div className="w-full rounded-xl overflow-hidden">
                  <img
                    src="/images/blogue-rat-dans-la-maison-que-faire-1.webp"
                    alt="Galetas en bois sombre sous toiture en tôle avec traces d'activité de rats en Guadeloupe"
                    className="w-full object-cover max-h-80 md:max-h-96"
                   loading="eager" fetchPriority="high" />
                </div>

                {/* H2 - ÉTAPE 1 */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Étape 1 : confirmez (rat, souris, ou autre chose ?)
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Cherchez les indices le long des murs et dans la cuisine :
                </p>

                <ul className="space-y-3 list-none pl-0">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Crottes :</strong> grain de riz = souris ; olive, 1-2 cm = rat.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Traces de gras sombres</strong> le long des plinthes et poutres (le passage répété du rat marque).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Emballages rongés</strong> avec traces de dents nettes.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Bruits nocturnes :</strong> légers et rapides = souris ; lourds, dans le galetas = rat noir, l&rsquo;excellent grimpeur qui domine nos combles.</span>
                  </li>
                </ul>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Un bruit uniquement au crépuscule et à l&rsquo;aube dans la toiture peut aussi être une chauve-souris — d&rsquo;où l&rsquo;intérêt d&rsquo;identifier avant d&rsquo;agir.
                </p>

                {/* H2 - ÉTAPE 2 */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Étape 2 : coupez le buffet (dès ce soir)
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Le rat reste là où il mange et boit.
                </p>

                <ol className="space-y-5 list-none pl-0">
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">1</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Aliments en boîtes rigides fermées.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Le carton et les sachets ne résistent pas 5 minutes — pensez aussi aux croquettes et au sac de riz.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">2</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Vaisselle faite le soir.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Pas de restes accessibles.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">3</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Poubelle à couvercle, sortie souvent.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">4</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Pas d&rsquo;eau accessible.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Fuites, gamelles la nuit, coupelles de plantes.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">5</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Fruits du jardin ramassés.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Mangues et fruits à pain tombés sont des restaurants à ciel ouvert.</p>
                    </div>
                  </li>
                </ol>

                {/* H2 - ÉTAPE 3 */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Étape 3 : les erreurs à ne pas faire
                </h2>

                <div className="space-y-4">
                  <p className="text-foreground-700 text-base leading-relaxed">
                    <strong className="text-accent-500">Ne bouchez pas tout de suite les trous :</strong> il faut d&rsquo;abord savoir par où ils passent (le professionnel lit ces passages pour placer le traitement) — et un rat muré à l&rsquo;intérieur meurt dans une cloison, avec l&rsquo;odeur que vous imaginez pendant des semaines.
                  </p>
                  <p className="text-foreground-700 text-base leading-relaxed">
                    <strong className="text-accent-500">N&rsquo;utilisez pas de poison grand public à l&rsquo;aveugle :</strong> mal dosé et mal placé, il fait le tri des rats les plus résistants, tue lentement dans des endroits inaccessibles, et met vos animaux domestiques en danger.
                  </p>
                  <p className="text-foreground-700 text-base leading-relaxed">
                    <strong className="text-accent-500">Ne comptez pas uniquement sur le chat :</strong> un bon chat dissuade les souris ; contre un rat noir adulte, la plupart déclarent forfait.
                  </p>
                </div>

                {/* H2 - ÉTAPE 4 */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Étape 4 : évaluez l&rsquo;ampleur
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  <strong className="text-foreground-950">Un rat isolé</strong> (traces localisées, bruit occasionnel) peut se régler avec 2-3 tapettes à rat bien placées — perpendiculaires au mur, appât fixé (beurre de cacahuète, morceau de banane bien mûre), gants pour manipuler, patience de quelques jours car le rat se méfie de tout objet nouveau.
                </p>

                <p className="text-foreground-700 text-base leading-relaxed">
                  <strong className="text-foreground-950">Une colonie installée</strong> (crottes nombreuses et fraîches, bruits chaque nuit, rat vu en plein jour — signe que la population sature) ne se règle plus à la tapette : chaque jour qui passe, c&rsquo;est des câbles rongés (première cause d&rsquo;incendie électrique), des aliments contaminés, et un risque sanitaire réel.
                </p>

                {/* H2 - LEPTOSPIROSE */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Le risque qu&rsquo;on ne voit pas : la leptospirose
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  L&rsquo;urine de rat transmet la leptospirose, et la Guadeloupe est l&rsquo;une des régions de France les plus touchées — des dizaines de cas par an, parfois graves. Eau stagnante, surfaces souillées, objets stockés dans un galetas infesté : ne manipulez rien à mains nues, lavez-vous après tout contact, et lisez notre dossier complet.
                </p>

                <Link
                  to="/blogue/leptospirose-guadeloupe"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold text-base hover:text-secondary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-article-line w-5 h-5 flex items-center justify-center"></i>
                  Leptospirose en Guadeloupe : le vrai danger des rats
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>

                {/* H2 - QUAND APPELER UN PRO */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Quand appeler un professionnel
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Si les signes indiquent une colonie, si le problème revient à chaque saison, si vous avez un commerce alimentaire (obligation HACCP), ou si vous ne voulez simplement pas gérer ça vous-même : notre intervention comprend le diagnostic, le traitement sécurisé (postes fermés à clé, hors de portée des enfants et animaux), le colmatage des accès et le suivi jusqu&rsquo;à éradication — entre 75 et 260 € pour un logement standard.
                </p>
              </div>
            </RevealWrapper>
          </article>
        </div>
      </section>

      {/* BLOC NOTRE SOLUTION */}
      <section className="bg-background-100 py-12 md:py-16">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="bg-secondary-100 border-l-4 border-secondary-500 rounded-r-xl p-6 md:p-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                  Notre solution
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Nous traitons les rats en Guadeloupe avec un protocole sécurisé : postes d&rsquo;appâtage fermés à clé, diagnostic des accès, colmatage, et suivi jusqu&rsquo;à éradication complète. Particuliers et professionnels, partout sur l&rsquo;île, sous 24-48 h.
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-3 flex-wrap">
                  <Link
                    to="/deratisation-guadeloupe"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-secondary-500 text-white hover:bg-secondary-600 cursor-pointer"
                  >
                    Notre service dératisation
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                  <Link
                    to="/tarifs"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-white text-foreground-950 border border-background-300 hover:border-secondary-300 cursor-pointer"
                  >
                    Voir tous nos tarifs
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <section className="bg-primary-500 py-14 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-8">
            Un doute sur l&rsquo;ampleur ?
          </h2>
          <p className="text-white/80 text-base leading-relaxed max-w-xl mx-auto mb-8">
            Photo des crottes ou dégâts sur WhatsApp : diagnostic gratuit en 2 h.
          </p>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-8 py-3.5 rounded-lg font-semibold text-base transition-all duration-200 bg-whatsapp-500 text-white hover:bg-whatsapp-600 cursor-pointer"
          >
            <i className="ri-whatsapp-line text-xl w-5 h-5 flex items-center justify-center"></i>
            WhatsApp direct
          </a>
        </div>
      </section>

      {/* MAILLAGE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/deratisation-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Notre service dératisation
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Diagnostic, traitement sécurisé, colmatage — partout en Guadeloupe
              </span>
            </Link>
            <Link
              to="/blogue/leptospirose-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Leptospirose en Guadeloupe : le vrai danger des rats
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Transmission, symptômes, prévention — notre dossier complet
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Consultez nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit en 2 h
              </span>
            </Link>
            <Link
              to="/blogue"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Retour au blogue
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Tous nos guides anti-nuisibles en Guadeloupe
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="blogue-rat" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}